import {
  DEFAULT_ROLE_PERMISSIONS,
  PROTECTED_PERMISSIONS,
  type Permission,
  type Role,
} from '../vocabulary';

/**
 * What a role is allowed to do.
 *
 * The matrix is runtime configuration, editable from Configuración global ›
 * Roles y permisos, and it drives navigation and every control's visibility.
 *
 * What it is **not** is a security boundary. A user who edits the client's copy
 * of this matrix gains nothing: the Firestore rules authorise on custom claims
 * minted by a Cloud Function and cannot be changed from the app, and every
 * privileged callable re-reads the stored matrix server-side before acting. This
 * policy decides what to *offer*; the server decides what to *allow*. Keeping
 * that distinction is the reason the matrix can be safely editable at all.
 */

export type PermissionMatrix = Readonly<Record<Role, readonly Permission[]>>;

/** Whether a role holds a permission under the given matrix. */
export const can = (
  matrix: PermissionMatrix,
  role: Role,
  permission: Permission,
): boolean => (matrix[role] ?? []).includes(permission);

/**
 * Los permisos de **una persona**: los de su rol, más los suyos, menos los que se
 * le quitaron.
 *
 * El rol reparte el paquete que corresponde a un trabajo, y eso sigue siendo lo
 * normal para casi todo el mundo. Lo que no sabía decir el modelo era la
 * excepción, que en una empresa de 338 personas es constante: «RR.HH., **además**
 * ver salario», «administradora de área, **pero no** expedientes». Sin esto, la
 * única forma de conceder algo suelto era ampliar el rol — y ampliarlo se lo
 * amplía a todos los que lo tienen.
 *
 * **El orden importa: primero suma, después resta.** Quitar gana siempre, incluso
 * sobre un permiso que alguien acaba de conceder a esa persona, porque una
 * revocación es una decisión sobre alguien concreto y una concesión también, pero
 * de las dos la que protege es la que quita.
 *
 * **Y no es una frontera de seguridad**, igual que el resto de este archivo: esto
 * decide qué se ofrece. Lo que se permite lo revalida cada callable contra la
 * misma cuenta, y las reglas de Firestore —que no pueden leer una matriz— ponen
 * un piso más grueso, el del rol.
 */
export const effectivePermissions = (
  matrix: PermissionMatrix,
  role: Role,
  grants?: {
    readonly extra?: readonly Permission[];
    readonly revoked?: readonly Permission[];
  },
): readonly Permission[] => {
  const base = matrix[role] ?? [];
  const conExtras = new Set([...base, ...(grants?.extra ?? [])]);
  for (const permiso of grants?.revoked ?? []) conExtras.delete(permiso);
  return [...conExtras];
};

/** Si una persona concreta tiene un permiso, contando sus excepciones. */
export const personCan = (
  matrix: PermissionMatrix,
  role: Role,
  permission: Permission,
  grants?: {
    readonly extra?: readonly Permission[];
    readonly revoked?: readonly Permission[];
  },
): boolean => effectivePermissions(matrix, role, grants).includes(permission);

/** Whether a role holds every one of the listed permissions. */
export const canAll = (
  matrix: PermissionMatrix,
  role: Role,
  permissions: readonly Permission[],
): boolean => permissions.every((p) => can(matrix, role, p));

/** Whether a role holds at least one of the listed permissions. */
export const canAny = (
  matrix: PermissionMatrix,
  role: Role,
  permissions: readonly Permission[],
): boolean => permissions.some((p) => can(matrix, role, p));

export type MatrixEditError =
  | { readonly kind: 'protected'; readonly role: Role; readonly permission: Permission }
  | { readonly kind: 'unknown-role'; readonly role: string };

/**
 * Toggles one permission for one role.
 *
 * Refuses to remove a protected permission. The handoff calls this out for the
 * superuser and `features`: taking it away removes the only route back to the
 * permissions screen, so the account locks itself out with no in-app recovery.
 * Guarding it here means the rule holds no matter which screen attempts the edit.
 */
export const togglePermission = (
  matrix: PermissionMatrix,
  role: Role,
  permission: Permission,
):
  | { readonly ok: true; readonly matrix: PermissionMatrix }
  | { readonly ok: false; readonly error: MatrixEditError } => {
  const current = matrix[role];
  if (current === undefined) {
    return { ok: false, error: { kind: 'unknown-role', role } };
  }

  const held = current.includes(permission);

  if (held && isProtected(role, permission)) {
    return { ok: false, error: { kind: 'protected', role, permission } };
  }

  const next = held
    ? current.filter((p) => p !== permission)
    : [...current, permission];

  return { ok: true, matrix: { ...matrix, [role]: next } };
};

/** Whether this role may never give up this permission. */
export const isProtected = (role: Role, permission: Permission): boolean =>
  (PROTECTED_PERMISSIONS[role] ?? []).includes(permission);

/** Restores one role's factory defaults — the "Restaurar original" button. */
export const resetRole = (matrix: PermissionMatrix, role: Role): PermissionMatrix => ({
  ...matrix,
  [role]: [...DEFAULT_ROLE_PERMISSIONS[role]],
});

/**
 * Normalises a matrix read from storage. Missing roles fall back to defaults and
 * unrecognised permission strings are dropped, so a matrix written by a newer
 * build — or hand-edited badly — degrades to something coherent instead of
 * leaving a role undefined and every `can()` call silently false.
 *
 * Protected permissions are re-added if absent. Without this, a matrix that
 * somehow lost `features` for the superuser would be unrecoverable in-app.
 */
export const normalizeMatrix = (
  raw: Readonly<Record<string, readonly string[]>>,
  knownPermissions: readonly Permission[],
  roles: readonly Role[],
): PermissionMatrix => {
  const normalized: Record<string, readonly Permission[]> = {};

  for (const role of roles) {
    const stored = raw[role];
    if (stored === undefined) {
      normalized[role] = [...DEFAULT_ROLE_PERMISSIONS[role]];
      continue;
    }

    const kept = stored.filter((p): p is Permission =>
      (knownPermissions as readonly string[]).includes(p),
    );

    const withProtected = [...new Set([...kept, ...(PROTECTED_PERMISSIONS[role] ?? [])])];
    normalized[role] = withProtected;
  }

  return normalized as PermissionMatrix;
};

/** How many permissions a role holds — the counter next to each role's tab. */
export const permissionCount = (matrix: PermissionMatrix, role: Role): number =>
  (matrix[role] ?? []).length;
